import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  Store,
  StoreData,
  StoreListParams,
  Stores,
  StoresData,
  StoreUpdatePayload,
} from '@/libs/interfaces'
import { storesApiMock } from '@/libs/mocks/handlers/stores'

const DEFAULT_PAYMENT_DAYS = 30

// The backend keeps payment options beside the store; the store form reads and writes them
// as part of the store. These two helpers translate between the shapes.
const withPaymentOptions = (data: StoreData): StoreData => ({
  ...data,
  store: {
    ...data.store,
    storePaymentOptions: data.storePaymentOptions?.[0] ?? data.store?.storePaymentOptions,
  },
})

export const toStoreUpdateBody = (payload: Omit<StoreUpdatePayload, 'orgID'>) => {
  const { bankgiro, plusgiro, paymentdays, reminderFee, ...storeFields } = payload
  const hasPaymentOptions = [bankgiro, plusgiro, paymentdays, reminderFee].some(
    (value) => value !== undefined && value !== '',
  )
  return {
    ...storeFields,
    ...(hasPaymentOptions && {
      storePaymentOptions: {
        bankgiro: bankgiro || undefined,
        plusgiro: plusgiro || undefined,
        paymentdays: paymentdays ?? DEFAULT_PAYMENT_DAYS,
        reminderFee,
      },
    }),
  }
}

const storesApiReal = {
  create: async (payload: Partial<Stores>): Promise<CreateApiResult<Stores>> => {
    return api
      .post<ApiResponse<Stores>>(URLs.Stores.create(payload.orgID || ''), payload)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  getStores: async (orgID: string): Promise<Store[]> => {
    return await api
      .get<{ data: { stores: Store[] } }>(URLs.STORES(orgID))
      .then((response) => response?.data?.data?.stores)
  },
  byID: async (params: Stores, skipErrorRedirect?: boolean): Promise<StoreData> => {
    return await api
      .get<StoreData>(
        URLs.Stores.byID(params?.orgID || '', params?.storeID || ''),
        undefined,
        undefined,
        skipErrorRedirect,
      )
      .then((response) => withPaymentOptions(response.data))
  },
  delete: async (payload: Partial<Stores>): Promise<Stores> => {
    return api
      .delete<Stores>(URLs.Stores.byID(payload?.orgID || '', payload?.storeID || ''))
      .then((response) => response.data)
  },
  list: async (params?: StoreListParams): Promise<Stores[]> => {
    const { orgID = '', ...queryParams } = params || {}
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<StoresData>>(URLs.Stores.list(orgID, query))
      .then((response) => response.data?.data?.stores || [])
  },
  listCursor: async (
    params: StoreListParams,
  ): Promise<CursorPaginatedResponse<Partial<Stores>>> => {
    const { orgID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<StoresData>>(URLs.Stores.list(orgID ?? '', query))
      .then((response) => response?.data?.data)

    return {
      data: response.stores,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  update: async (payload: StoreUpdatePayload) => {
    const { orgID, ...storePatch } = payload
    return await api
      .patch(URLs.Stores.byID(orgID || '', payload.storeID || ''), toStoreUpdateBody(storePatch))
      .then((response) => response.data)
  },
}

export const storesApi: typeof storesApiReal = __IS_MOCK_MODE__ ? storesApiMock : storesApiReal
